import {
  Check, ExternalLink, Loader2, Pencil, RefreshCw, ShieldCheck, X,
} from "lucide-react";
import {
  useCallback, useEffect, useMemo, useState,
} from "react";
import { toast } from "sonner";
import {
  useUpdateEmployeeIbanMutation,
  useVerifyEmployeeIbanMutation,
} from "@/shared/hooks/useEmployeeIbanMutations";
import { captureMessage } from "@/shared/services/sentry.service";
import { Button } from "@/shared/ui/Button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/shared/ui/Dialog";
import { Input } from "@/shared/ui/Input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/Tooltip";
import {
  broadcastBridgeTransferResult,
  clearBridgeTransferResult,
  openBridgeConsentWindow,
  subscribeToBridgeTransferResults,
  watchBridgeConsentWindow,
} from "@/shared/utils/bridgeTransferChannel";
import { extractApiErrorDetail, isConflictError, toastErrorIfNotForbidden } from "@/shared/utils/errorHandling";
import { useBridgeReleaseMutation, useBridgeTransferMutation } from "../hooks/useAccountingQueries";
import type { BridgeTransferValidationError } from "../services/accounting.service";
import type { BankMovement } from "../types/accounting.types";

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  movement: BankMovement | null
};

const maskIban = (iban: string | null): string => {
  if (!iban) return "-";
  const cleaned = iban.replace(/\s+/g, "");
  if (cleaned.length <= 8) return cleaned;
  const start = cleaned.slice(0, 4);
  const end = cleaned.slice(-4);
  const middle = cleaned.slice(4, -4).replace(/./g, "*");
  // Format in groups of 4
  const full = start + middle + end;
  return full.replace(/(.{4})/g, "$1 ").trim();
};

const nextBusinessDay = (date: Date, offset: number): Date => {
  const d = new Date(date);
  let found = false;
  while (!found) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() >= 2 && d.getDay() <= 5) found = true;
  }
  let added = 0;
  while (added < offset) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() >= 2 && d.getDay() <= 5) added++;
  }
  return d;
};

const formatDate = (date: Date): string =>
  date.toLocaleDateString("fr-FR", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

const formatAmount = (value: number) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(value);

const BridgeTransferDialog = ({ open, onOpenChange, movement }: Props) => {
  const bridgeTransferMutation = useBridgeTransferMutation();
  const releaseMutation = useBridgeReleaseMutation();
  const verifyIbanMutation = useVerifyEmployeeIbanMutation();
  const updateIbanMutation = useUpdateEmployeeIbanMutation();

  const [validationErrors, setValidationErrors] = useState<BridgeTransferValidationError[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editIban, setEditIban] = useState("");
  const [editBic, setEditBic] = useState("");
  const [localIban, setLocalIban] = useState<string | null>(null);
  const [localBic, setLocalBic] = useState<string | null>(null);
  const [localIbanVerified, setLocalIbanVerified] = useState(false);
  const [localIbanReason, setLocalIbanReason] = useState<string | null>(null);
  const [consentOpened, setConsentOpened] = useState(false);
  // Renseigné quand le navigateur a bloqué l'ouverture de la fenêtre Bridge.
  const [blockedConsentUrl, setBlockedConsentUrl] = useState<string | null>(null);
  const [transferResult, setTransferResult] = useState<
    "validated" | "confirmed" | "confirmed_no_date" | "failed" | null
  >(null);

  const info = movement?.clientIbanInfo ?? null;
  const clientId = info?.clientId ?? null;
  const movementId = movement?.id ?? null;

  // Sync local state when movement changes
  useEffect(() => {
    if (open && info) {
      setLocalIban(info.iban);
      setLocalBic(info.bic);
      setLocalIbanVerified(info.ibanVerified);
      setLocalIbanReason(info.ibanReason);
      setValidationErrors([]);
      setErrorMessage(null);
      setIsEditing(false);
      setConsentOpened(false);
      setBlockedConsentUrl(null);
      setTransferResult(null);
    }
  }, [open, info]);

  // Résultat renvoyé par la fenêtre de consentement (BridgeCallbackPage) tant
  // que le dialog reste ouvert : on remplace « en attente » par l'issue réelle.
  useEffect(() => {
    if (!open || !movementId) return;
    return subscribeToBridgeTransferResults((message) => {
      if (message.movementId !== movementId) return;

      switch (message.phase) {
        // Abandon ou libération déjà effectuée : on redonne la main pour relancer
        // le virement. Surtout pas l'état « échoué », qui ferait passer une
        // annulation pour un rejet bancaire.
        case "abandoned":
        case "released":
          setConsentOpened(false);
          setBlockedConsentUrl(null);
          setTransferResult(null);
          return;
        // Retour de Bridge : l'issue n'est pas encore connue côté URL.
        case "returned":
          setTransferResult("validated");
          return;
        case "confirmed":
          if (message.status === "failure") {
            setTransferResult("failed");
            return;
          }
          setTransferResult(message.issueDateRecorded === false ? "confirmed_no_date" : "confirmed");
          return;
      }
    });
  }, [open, movementId]);

  const executionDate = useMemo(() => nextBusinessDay(new Date(), 2), []);

  const handleVerifyIban = useCallback(async () => {
    if (!clientId) return;
    try {
      const result = await verifyIbanMutation.mutateAsync(clientId);
      setLocalIbanVerified(result.iban_verified);
      setLocalIbanReason(result.iban_reason ?? null);
      if (result.iban) setLocalIban(result.iban);
      if (result.bic_calcule) setLocalBic(result.bic_calcule);
      if (result.iban_verified) {
        toast.success("IBAN vérifié avec succès");
      }
      else {
        toast.error(result.iban_reason ?? "L'IBAN n'a pas pu être vérifié");
      }
    }
    catch (error) {
      toastErrorIfNotForbidden(error, "Erreur lors de la vérification de l'IBAN");
    }
  }, [clientId, verifyIbanMutation]);

  const handleSaveIban = useCallback(async () => {
    if (!clientId) return;
    try {
      const result = await updateIbanMutation.mutateAsync({
        employeeId: clientId,
        data: { iban: editIban, bic: editBic, verify: true },
      });
      setLocalIban(result.iban);
      setLocalBic(result.bic || editBic);
      setLocalIbanVerified(result.iban_verified);
      setLocalIbanReason(result.iban_reason ?? null);
      setIsEditing(false);
      if (result.iban_verified) {
        toast.success("IBAN/BIC mis à jour et vérifié");
      }
      else {
        toast.warning(result.iban_reason ?? "IBAN/BIC enregistré, mais l'IBAN n'a pas pu être vérifié.");
      }
    }
    catch (error) {
      toastErrorIfNotForbidden(error, "Erreur lors de la mise à jour de l'IBAN/BIC");
    }
  }, [clientId, editIban, editBic, updateIbanMutation]);

  // Fenêtre bloquée : le payment request existe déjà chez Bridge. On le libère
  // (l'API vérifie l'absence de consentement) pour que le mouvement redevienne
  // réinitiable, plutôt que de le laisser figé en « Initié » (ADM-864).
  const handleCancelInitiation = useCallback(async () => {
    if (!movement) return;
    try {
      await releaseMutation.mutateAsync({ bankMovementId: movement.id });
      setBlockedConsentUrl(null);
      setConsentOpened(false);
      setTransferResult(null);
      // Les autres onglets n'ont aucun autre moyen d'apprendre que l'initiation
      // a disparu : sans ça, ils continueraient d'afficher « Initié ».
      broadcastBridgeTransferResult({ movementId: movement.id, phase: "released" });
      toast.success("Initiation annulée : le virement peut être relancé.");
    }
    catch (error) {
      const detail = isConflictError(error)
        ? extractApiErrorDetail(error) ?? "Le consentement a déjà été donné chez Bridge."
        : extractApiErrorDetail(error) ?? "L'initiation n'a pas pu être annulée.";
      setErrorMessage(detail);
      toastErrorIfNotForbidden(error, detail);
    }
  }, [movement, releaseMutation]);

  const handleConfirm = useCallback(async () => {
    if (!movement) return;
    setValidationErrors([]);
    setErrorMessage(null);
    try {
      const result = await bridgeTransferMutation.mutateAsync(movement.id);
      if (result.validation_errors?.length) {
        setValidationErrors(result.validation_errors);
        return;
      }
      if (result.error) {
        // Remonte le message exact du back (ex. « Votre banque n'accepte pas la
        // gestion externe des virements sortants ») plutôt qu'un message générique.
        setErrorMessage(result.error);
        toast.error(result.error);
        return;
      }
      if (result.consent_url) {
        clearBridgeTransferResult(movement.id);
        const consentWindow = openBridgeConsentWindow(result.consent_url);
        if (consentWindow) {
          watchBridgeConsentWindow(consentWindow, movement.id);
          setConsentOpened(true);
          toast.success("Le lien de consentement a été ouvert");
        }
        else {
          captureMessage("[BridgeTransfer] Fenêtre de consentement bloquée par le navigateur", "warning");
          setBlockedConsentUrl(result.consent_url);
        }
        return;
      }
      // Réponse 2xx sans consent_url ni erreur : cas non prévu, on le remonte
      // au lieu de laisser l'utilisateur sans aucun retour.
      captureMessage("[BridgeTransfer] Réponse bridge_transfer sans consent_url", "warning");
      const unexpected = "Le lien de consentement Bridge n'a pas été reçu. Veuillez réessayer.";
      setErrorMessage(unexpected);
      toast.error(unexpected);
    }
    catch (err) {
      // eslint-disable-next-line no-console
      console.error("[BridgeTransfer] bridge_transfer failed:", err);
      if (err && typeof err === "object" && "response" in err) {
        const axiosErr = err as { response?: { data?: { error?: string, validation_errors?: BridgeTransferValidationError[] } } };
        if (axiosErr.response?.data?.validation_errors?.length) {
          setValidationErrors(axiosErr.response.data.validation_errors);
          return;
        }
        if (axiosErr.response?.data?.error) {
          setErrorMessage(axiosErr.response.data.error);
          toast.error(axiosErr.response.data.error);
          return;
        }
      }
      const fallback = err instanceof Error ? err.message : "Erreur lors du virement";
      setErrorMessage(fallback);
      toast.error(fallback);
    }
  }, [movement, bridgeTransferMutation]);

  if (!movement || !info) return null;

  const displayIban = localIban ?? info.iban;
  const displayBic = localBic ?? info.bic;
  const isVerified = localIbanVerified;
  const hasIban = Boolean(displayIban?.trim());
  const hasBic = Boolean(displayBic?.trim());
  // ADM-700 : raison renvoyée par le back (API IBAN / contrôle de format) quand
  // l'IBAN n'est pas vérifié, pour distinguer « invalide » de « non vérifié ».
  const ibanIssue = !isVerified ? localIbanReason : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={validationErrors.length > 0 ? "sm:max-w-2xl" : "sm:max-w-lg"}>
        <DialogHeader>
          <DialogTitle>Virement Bridge</DialogTitle>
          <DialogDescription>
            Initier un virement bancaire via Bridge pour ce mouvement
          </DialogDescription>
        </DialogHeader>

        {consentOpened || blockedConsentUrl
          ? (
            <div className="space-y-3 py-2">
              {blockedConsentUrl && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                  <p className="text-sm font-medium text-amber-800">Fenêtre Bridge bloquée par le navigateur</p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Le virement est prêt : ouvrez le lien de consentement pour le finaliser,
                    ou annulez l'initiation pour rendre le mouvement réinitiable.
                  </p>
                  <div className="flex items-center gap-1.5 mt-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs"
                      onClick={() => {
                        if (movement) clearBridgeTransferResult(movement.id);
                        const consentWindow = openBridgeConsentWindow(blockedConsentUrl);
                        if (consentWindow && movement) {
                          watchBridgeConsentWindow(consentWindow, movement.id);
                          setBlockedConsentUrl(null);
                          setConsentOpened(true);
                        }
                      }}
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      Ouvrir le lien Bridge
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-8 text-xs"
                      onClick={handleCancelInitiation}
                      disabled={releaseMutation.isPending}
                    >
                      {releaseMutation.isPending
                        ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        : <X className="h-3 w-3 mr-1" />}
                      Annuler l'initiation
                    </Button>
                  </div>
                </div>
              )}
              {consentOpened && transferResult === null && (
                <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
                  <ExternalLink className="h-5 w-5 text-emerald-600 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-emerald-800">Lien de consentement ouvert</p>
                    <p className="text-xs text-emerald-700 mt-0.5">
                      Complétez la validation dans la fenêtre Bridge. La confirmation s'affichera ici automatiquement.
                    </p>
                  </div>
                </div>
              )}
              {transferResult === "validated" && (
                <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
                  <Loader2 className="h-5 w-5 animate-spin text-emerald-600 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-emerald-800">Virement Bridge validé</p>
                    <p className="text-xs text-emerald-700 mt-0.5">
                      Enregistrement de la date d'émission sur le mouvement...
                    </p>
                  </div>
                </div>
              )}
              {(transferResult === "confirmed" || transferResult === "confirmed_no_date") && (
                <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
                  <Check className="h-5 w-5 text-emerald-600 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-emerald-800">Virement Bridge confirmé</p>
                    <p className="text-xs text-emerald-700 mt-0.5">
                      {transferResult === "confirmed"
                        ? "Le virement a été validé. La date d'émission a été enregistrée sur le mouvement."
                        : "Le virement a été validé, mais la date d'émission n'a pas pu être enregistrée sur le mouvement."}
                    </p>
                  </div>
                </div>
              )}
              {transferResult === "failed" && (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                  <X className="h-5 w-5 text-red-600 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-800">Virement Bridge échoué</p>
                    <p className="text-xs text-red-700 mt-0.5">
                      Le virement a échoué ou a été annulé. Vous pouvez le réinitier depuis le mouvement.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )
          : (
            <div className="space-y-4 py-2">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div>
                  <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">Bénéficiaire</div>
                  <div className="font-medium text-slate-800 mt-0.5">{info.clientName}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">Montant</div>
                  <div className="font-semibold text-rose-700 mt-0.5">{formatAmount(movement.debit ?? 0)}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">Date d'exécution</div>
                  <div className="text-slate-800 mt-0.5">{formatDate(executionDate)}</div>
                </div>
              </div>

              {/* IBAN / BIC */}
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
                {isEditing
                  ? (
                    <div className="space-y-2">
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-slate-600">IBAN</label>
                        <Input
                          value={editIban}
                          onChange={(e) => setEditIban(e.target.value)}
                          placeholder="FR76 ..."
                          className="h-8 text-sm font-mono"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-slate-600">BIC</label>
                        <Input
                          value={editBic}
                          onChange={(e) => setEditBic(e.target.value)}
                          placeholder="BNPAFRPP"
                          className="h-8 text-sm font-mono"
                        />
                      </div>
                      <div className="flex items-center gap-1.5 pt-1">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={handleSaveIban}
                          disabled={updateIbanMutation.isPending || !editIban.trim()}
                        >
                          {updateIbanMutation.isPending
                            ? (
                              <Loader2 className="h-3 w-3 animate-spin mr-1" />
                            )
                            : (
                              <Check className="h-3 w-3 mr-1" />
                            )}
                          Enregistrer
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs"
                          onClick={() => setIsEditing(false)}
                        >
                          <X className="h-3 w-3 mr-1" />
                          Annuler
                        </Button>
                      </div>
                    </div>
                  )
                  : (
                    <>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-xs font-medium text-slate-500">IBAN</div>
                          <div className="text-sm font-mono text-slate-800 mt-0.5">
                            {maskIban(displayIban)}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {isVerified
                            ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 border border-emerald-200">
                                    <ShieldCheck className="h-3 w-3" />
                                    Vérifié
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>IBAN vérifié via l'API</TooltipContent>
                              </Tooltip>
                            )
                            : ibanIssue
                              ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-700 border border-red-200">
                                      Invalide
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>{ibanIssue}</TooltipContent>
                                </Tooltip>
                              )
                              : (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 border border-amber-200">
                                      Non vérifié
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>L'IBAN n'a pas encore été vérifié</TooltipContent>
                                </Tooltip>
                              )}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-slate-500">BIC</div>
                        <div className="text-sm font-mono text-slate-800 mt-0.5">{displayBic || "-"}</div>
                      </div>
                      <div className="flex items-center gap-1.5 pt-1">
                        {!isVerified && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={handleVerifyIban}
                            disabled={verifyIbanMutation.isPending}
                          >
                            {verifyIbanMutation.isPending
                              ? (
                                <Loader2 className="h-3 w-3 animate-spin mr-1" />
                              )
                              : (
                                <RefreshCw className="h-3 w-3 mr-1" />
                              )}
                            Vérifier l'IBAN
                          </Button>
                        )}
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs"
                          onClick={() => {
                            setEditIban(displayIban ?? "");
                            setEditBic(displayBic ?? "");
                            setIsEditing(true);
                          }}
                        >
                          <Pencil className="h-3 w-3 mr-1" />
                          Modifier
                        </Button>
                      </div>
                      {ibanIssue && (
                        <p className="text-[11px] text-red-600 leading-snug">
                          {ibanIssue}
                        </p>
                      )}
                    </>
                  )}
              </div>

              {/* Validation errors */}
              {validationErrors.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-sm font-medium text-red-600">
                    {validationErrors.length}
                    {" "}
                    erreur(s) de validation
                  </p>
                  <div className="max-h-40 overflow-auto rounded border border-red-200 bg-red-50">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-red-100 text-left">
                        <tr>
                          <th className="px-2 py-1.5 font-medium text-red-800">Salarié</th>
                          <th className="px-2 py-1.5 font-medium text-red-800">Champ</th>
                          <th className="px-2 py-1.5 font-medium text-red-800">Message</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-red-200">
                        {validationErrors.map((e, i) => (
                          <tr key={i}>
                            <td className="px-2 py-1 text-red-700">{e.salarie}</td>
                            <td className="px-2 py-1 text-red-700">{e.champ}</td>
                            <td className="px-2 py-1 text-red-700">{e.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Error message */}
              {errorMessage && (
                <div className="rounded border border-red-200 bg-red-50 px-3 py-2">
                  <p className="text-sm text-red-700">{errorMessage}</p>
                </div>
              )}
            </div>
          )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {consentOpened || blockedConsentUrl ? "Fermer" : "Annuler"}
          </Button>
          {!consentOpened && !blockedConsentUrl && (
            <Button
              type="button"
              variant="action"
              onClick={handleConfirm}
              disabled={bridgeTransferMutation.isPending || isEditing || !hasIban || !hasBic}
            >
              {bridgeTransferMutation.isPending
                ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Envoi en cours...
                  </span>
                )
                : (
                  "Confirmer le virement"
                )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BridgeTransferDialog;
